import React, { useState, useEffect, useCallback } from 'react';
import collapseIn from '../assets/svgDrawer/svgs/collapseIn.svg';
import collapse from '../assets/svgDrawer/svgs/collapse.svg';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { resetFilters } from '../store/map/mapSlice';
import { useSidebarMenus } from '../hooks/useSidebarMenus';

function Sidebar({ userRole }) {
  const dispatch = useDispatch();
  const location = useLocation();

  const menus = useSidebarMenus(userRole);

  const [collapseState, setCollapseState] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const getActiveSubPath = useCallback((subMenus, pathname) => {
    if (!Array.isArray(subMenus) || !subMenus.length) return null;
    const exactMatch = subMenus.find((sub) => pathname === sub.path);
    if (exactMatch) return exactMatch.path;
    const prefixMatches = subMenus.filter((sub) =>
      pathname.startsWith(`${sub.path}/`),
    );
    if (!prefixMatches.length) return null;
    return prefixMatches.sort((a, b) => b.path.length - a.path.length)[0].path;
  }, []);

  const pathToMenuIndex = useCallback(
    (pathname, menus) => {
      return menus.findIndex((item) => {
        if (item.path === '/') {
          return pathname === '/';
        }
        if (item.subMenus?.length) {
          return Boolean(getActiveSubPath(item.subMenus, pathname));
        }
        return pathname === item.path || pathname.startsWith(item.path + '/');
      });
    },
    [getActiveSubPath],
  );

  const [activeMenu, setActiveMenu] = useState(() => {
    const index = pathToMenuIndex(location.pathname, menus);
    return index === -1 ? 0 : index;
  });

  useEffect(() => {
    const index = pathToMenuIndex(location.pathname, menus);
    if (index !== -1) {
      setActiveMenu(index);
    }
  }, [location.pathname, menus, pathToMenuIndex]);

  const handleMenu = (index, hasSubMenus) => {
    if (hasSubMenus) {
      setActiveMenu(index);
      return;
    }
    if (!collapseState) {
      setCollapseState(true);
    }
    setActiveMenu(index);
  };

  const handleSubMenuClick = (index, subPath) => {
    if (subPath.startsWith('/maps')) {
      dispatch(resetFilters());
      window.dispatchEvent(new Event('map-submenu-reset'));
    }
    handleMenu(index, true);
  };

  const handleMouseEnter = (index) => {
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  return (
    <div style={{ height: `calc(100vh - 50px)` }} className="flex flex-row">
      {/* expanded */}
      {collapseState && (
        <div
          className={`flex flex-col justify-between w-[170px] overflow-y-auto shadow-xl`}
        >
          <div className="flex flex-col gap-3">
            {menus.map((el, i) => {
              const hasSubMenus = el.subMenus?.length > 0;
              const activeSubPath = hasSubMenus
                ? getActiveSubPath(el.subMenus, location.pathname)
                : null;
              const isSubMenuActive = hasSubMenus && Boolean(activeSubPath);
              const Icon =
                hoveredIndex === i || activeMenu === i || isSubMenuActive
                  ? el.icon_color
                  : el.icon;

              return (
                <div
                  key={el.path || el.label}
                  onMouseEnter={() => handleMouseEnter(i)}
                  onMouseLeave={handleMouseLeave}
                >
                  {hasSubMenus ? (
                    <div
                      className={`flex flex-row items-center px-3 py-1 border-r-2 cursor-pointer hover:bg-[#f1f5f9]
                      ${
                        hoveredIndex === i ||
                        activeMenu === i ||
                        isSubMenuActive
                          ? 'border-[#287094]'
                          : 'border-white'
                      }`}
                    >
                      <div className="flex justify-center items-center p-2">
                        <img className="w-5 h-5" src={Icon} alt={el.label} />
                      </div>
                      <p className="text-s text-left leading-3">{el.label}</p>
                    </div>
                  ) : (
                    <Link to={el.path}>
                      <div
                        onMouseEnter={() => handleMouseEnter(i)}
                        onMouseLeave={handleMouseLeave}
                        onClick={() => handleMenu(i, false)}
                        className={`flex flex-row items-center px-3 py-1 border-r-2 hover:bg-[#f1f5f9]
                        ${
                          hoveredIndex === i ||
                          activeMenu === i ||
                          isSubMenuActive
                            ? 'border-[#287094]'
                            : 'border-white'
                        }`}
                      >
                        <div className="flex justify-center items-center p-2">
                          <img className="w-5 h-5" src={Icon} alt={el.label} />
                        </div>
                        <p className="text-s text-left leading-3">{el.label}</p>
                      </div>
                    </Link>
                  )}
                  {hasSubMenus && hoveredIndex === i && (
                    <div className="flex flex-col gap-1 pl-9 pr-2 pb-1">
                      {el.subMenus.map((sub) => {
                        const subActive = activeSubPath === sub.path;
                        const SubIcon = subActive ? sub.icon_color : sub.icon;
                        return (
                          <Link
                            key={sub.path}
                            to={sub.path}
                            onClick={() => handleSubMenuClick(i, sub.path)}
                          >
                            <div
                              className={`flex flex-row items-center gap-2 py-1 px-2 rounded hover:bg-[#f1f5f9]
                              ${
                                subActive
                                  ? 'border-l-2 border-[#287094] bg-[#f1f5f9]'
                                  : 'border-l-2 border-transparent'
                              }`}
                            >
                              <img
                                className="w-4 h-4"
                                src={SubIcon}
                                alt={sub.label}
                              />
                              <p className="text-[13px] text-left leading-4">
                                {sub.label}
                              </p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex flex-row items-center px-3 py-2 mb-3 border-r-2 border-white hover:border-[#287094] hover:transition-all">
            <div
              onClick={(_) => setCollapseState(!collapseState)}
              className="flex flex-row justify-center items-center w-6"
            >
              <img
                className="w-5 h-5"
                src={collapseState ? collapseIn : collapse}
                alt={`logo-${collapse}`}
              />
            </div>
            <p className="text-xs text-left leading-3">Collapse</p>
          </div>
        </div>
      )}

      {/* not expanded */}
      {!collapseState && (
        <div
          className={`flex flex-col justify-between w-[70px] overflow-y-auto ${
            !collapseState ? 'shadow-lg' : ''
          } `}
        >
          <div className="flex flex-col ">
            {menus.map((el, i) => {
              const hasSubMenus = Array.isArray(el.subMenus);
              const activeSubPath = hasSubMenus
                ? getActiveSubPath(el.subMenus, location.pathname)
                : null;
              const isSubMenuActive = hasSubMenus && Boolean(activeSubPath);
              const Icon =
                hoveredIndex === i || activeMenu === i || isSubMenuActive
                  ? el.icon_color
                  : el.icon;

              return (
                <div
                  key={i}
                  className="relative"
                  onMouseEnter={() => handleMouseEnter(i)}
                  onMouseLeave={() => handleMouseLeave(i)}
                >
                  {hasSubMenus ? (
                    <div className="flex flex-col py-0.5 hover:bg-gray-50 border-r-2 border-white hover:border-[#287094] hover:transition-all cursor-pointer">
                      <div className="flex flex-row justify-center items-center h-12 p-2">
                        <img width="40%" src={Icon} alt={el.label} />
                      </div>
                    </div>
                  ) : (
                    <Link to={el.path}>
                      <div
                        onClick={() => handleMenu(i, false)}
                        className="flex flex-col py-0.5 hover:bg-gray-50 border-r-2 border-white hover:border-[#287094] hover:transition-all"
                      >
                        <div className="flex flex-row justify-center items-center h-12 p-2">
                          <img width="40%" src={Icon} alt={el.label} />
                        </div>
                      </div>
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex flex-col py-0.5 mb-3 border-r-2 border-white hover:bg-gray-50 hover:border-[#5890ad] hover:transition-all">
            <div
              onClick={(_) => setCollapseState(!collapseState)}
              className="flex flex-row justify-center items-center h-12 p-2"
            >
              <img
                width="40%"
                src={collapseState ? collapseIn : collapse}
                alt={`logo-${collapse}`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
Sidebar.propTypes = {
  userRole: PropTypes.string.isRequired,
};

export default Sidebar;
